import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { queryKeys } from "@/lib/api/queryKeys";
import type { JobStage } from "../types";
import type { UploadConsultaResult } from "../types";
import { uploadConsulta, type UploadConsultaOptions } from "../api/uploadConsulta";

export interface UseUploadConsultaResult {
  stage: JobStage;
  mutate: (opts: Omit<UploadConsultaOptions, "signal" | "onStageChange" | "token">) => void;
  abort: () => void;
  isPending: boolean;
  error: Error | null;
  data: UploadConsultaResult | undefined;
  reset: () => void;
}

export function useUploadConsulta(): UseUploadConsultaResult {
  const [stage, setStage] = useState<JobStage>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const { session } = useSession();

  const mutation = useMutation({
    mutationFn: async (opts: Omit<UploadConsultaOptions, "signal" | "onStageChange" | "token">) => {
      const token = await session?.getToken();
      if (!token) throw new Error("Usuário não autenticado.");
      const controller = new AbortController();
      abortRef.current = controller;
      return uploadConsulta({
        ...opts,
        token,
        signal: controller.signal,
        onStageChange: (s) => setStage(s as JobStage),
      });
    },
    onSuccess: () => {
      setStage("done");
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultas.all });
    },
    onError: () => {
      setStage("error");
    },
  });

  const abort = () => {
    abortRef.current?.abort();
    setStage("idle");
    mutation.reset();
  };

  const reset = () => {
    setStage("idle");
    mutation.reset();
  };

  return {
    stage,
    mutate: mutation.mutate,
    abort,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    reset,
  };
}
